// The billing module's data access, and nothing outside this folder uses it.
//
// Two calls, and they are deliberately not merged:
//
//   loadUsage()   `workspace_usage()` — one round trip for the whole screen,
//                 gated on `billing.view` inside PostgreSQL.
//   the requests  read through `lib/billing.ts`, which every other screen
//                 already uses. `plan_change_requests` has its own RLS policy;
//                 duplicating the query here would be a second place for that
//                 rule to be wrong, and the module boundary is not worth that.

import { supabase } from "../lib/supabase";
import { asAppError } from "../lib/entitlements";
import type { WorkspaceUsage } from "./types";

/** Raised when the database refused on permission grounds, rather than failed. */
export class BillingForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BillingForbiddenError";
  }
}

export const isForbidden = (e: unknown): e is BillingForbiddenError =>
  e instanceof BillingForbiddenError;

/**
 * The whole screen, in one request.
 *
 * The reader's own timezone goes with it so a draft written at 11pm in Kolkata
 * lands on the right bar — without one, everybody east of UTC reads a chart
 * shifted by a day and cannot tell. The monthly meters are *not* affected by
 * it; they reset on the UTC calendar month, which is what the database enforces
 * against, and `range.month_resets_at` says when.
 */
export async function loadUsage(
  workspaceId: string,
  days = 30,
): Promise<WorkspaceUsage> {
  let timezone = "UTC";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    // An engine with no resolved timezone gets UTC days, which is wrong by
    // hours rather than wrong by a day for most of the world.
  }

  const { data, error } = await supabase.rpc("workspace_usage", {
    p_workspace_id: workspaceId,
    p_days: days,
    p_tz: timezone,
  });

  if (error) {
    // PT403 is the permission refusal every gated RPC in this project raises
    // (migration 20260823000400 onwards). It is a different screen from a
    // failure — "you may not see this" and "we could not read it" call for
    // opposite reactions — so it is a different error type rather than a
    // string the UI has to pattern-match.
    if (error.code === "PT403") throw new BillingForbiddenError(error.message);
    throw asAppError(error);
  }

  return parse(data);
}

// ---------------------------------------------------------------- parsing
//
// The RPC returns jsonb, which arrives as `unknown`. Narrowed once, here, so no
// component holds an `any` and a missing key becomes a zero rather than a `NaN`
// on somebody's bill.

type Json = Record<string, unknown>;

const obj = (v: unknown): Json =>
  typeof v === "object" && v !== null ? (v as Json) : {};

/** A bigint past 2^53 arrives from PostgREST as a string, not a number. */
function num(v: unknown): number {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

/** null is UNLIMITED here, and collapsing it to 0 would invent a paywall. */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strOrNull = (v: unknown): string | null =>
  typeof v === "string" && v.length > 0 ? v : null;
const bool = (v: unknown): boolean => v === true;
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

function parse(raw: unknown): WorkspaceUsage {
  const d = obj(raw);
  const range = obj(d["range"]);
  const plan = d["plan"];
  const sub = d["subscription"];
  const ai = obj(d["ai"]);
  const aiMonth = obj(d["ai_month"]);
  const transfer = obj(d["transfer"]);
  const storage = obj(d["storage"]);
  const activity = obj(d["activity"]);
  const requests = obj(d["requests"]);

  return {
    range: {
      days: num(range["days"]),
      timezone: str(range["timezone"]),
      from: str(range["from"]),
      to: str(range["to"]),
      generated_at: str(range["generated_at"]),
      month_started_at: str(range["month_started_at"]),
      month_resets_at: str(range["month_resets_at"]),
    },
    plan:
      plan === null || plan === undefined
        ? null
        : {
            code: str(obj(plan)["code"]),
            name: str(obj(plan)["name"]),
            billing_period: str(obj(plan)["billing_period"]),
            price_cents: numOrNull(obj(plan)["price_cents"]),
            currency: str(obj(plan)["currency"]) || "USD",
            is_paid: bool(obj(plan)["is_paid"]),
          },
    subscription:
      sub === null || sub === undefined
        ? null
        : {
            status: str(obj(sub)["status"]),
            started_at: str(obj(sub)["started_at"]),
            current_period_end: strOrNull(obj(sub)["current_period_end"]),
            cancel_at: strOrNull(obj(sub)["cancel_at"]),
            renews_automatically: bool(obj(sub)["renews_automatically"]),
          },
    meters: arr(d["meters"]).map((row) => {
      const r = obj(row);
      const kind = str(r["kind"]);
      return {
        key: str(r["key"]),
        name: str(r["name"]),
        description: strOrNull(r["description"]),
        kind: kind === "limit" ? "limit" : "quota",
        unit: strOrNull(r["unit"]),
        implemented: bool(r["implemented"]),
        enabled: bool(r["enabled"]),
        limit: numOrNull(r["limit"]),
        used: num(r["used"]),
        resets_monthly: bool(r["resets_monthly"]),
      };
    }),
    ai: {
      generations: num(ai["generations"]),
      ok: num(ai["ok"]),
      failed: num(ai["failed"]),
      credits_spent: num(ai["credits_spent"]),
      plan_calls: num(ai["plan_calls"]),
      byok_calls: num(ai["byok_calls"]),
      tokens_in: num(ai["tokens_in"]),
      tokens_out: num(ai["tokens_out"]),
      tokens_cached: num(ai["tokens_cached"]),
    },
    ai_month: {
      credits_spent: num(aiMonth["credits_spent"]),
      generations: num(aiMonth["generations"]),
    },
    by_member: arr(d["by_member"]).map((row) => {
      const r = obj(row);
      return {
        user_id: str(r["user_id"]),
        email: strOrNull(r["email"]),
        generations: num(r["generations"]),
        credits_spent: num(r["credits_spent"]),
        byok_calls: num(r["byok_calls"]),
        failed: num(r["failed"]),
      };
    }),
    transfer: {
      measured_bytes: num(transfer["measured_bytes"]),
      attributed_bytes: num(transfer["attributed_bytes"]),
      objects: num(transfer["objects"]),
      has_any: bool(transfer["has_any"]),
      is_billed: bool(transfer["is_billed"]),
    },
    storage: {
      bytes: num(storage["bytes"]),
      objects: num(storage["objects"]),
      counts_rows_not_bucket: bool(storage["counts_rows_not_bucket"]),
    },
    activity: {
      posts_created: num(activity["posts_created"]),
      deliveries_published: num(activity["deliveries_published"]),
      media_uploaded: num(activity["media_uploaded"]),
      members: num(activity["members"]),
      connections: num(activity["connections"]),
    },
    daily: arr(d["daily"]).map((row) => {
      const r = obj(row);
      return {
        day: str(r["day"]),
        ai_generations: num(r["ai_generations"]),
        ai_credits: num(r["ai_credits"]),
        posts: num(r["posts"]),
        transfer_bytes: num(r["transfer_bytes"]),
      };
    }),
    requests: {
      open: num(requests["open"]),
      total: num(requests["total"]),
    },
  };
}
